import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow
} from '@react-google-maps/api';
import { driversAPI } from '../../services/api'; // Import from api.js

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

// Default center (Lagos)
const defaultCenter = {
  lat: 6.5244,
  lng: 3.3792
};

const LiveFleetOverview = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [error, setError] = useState(null);
  
  const mapRef = useRef(null);
  const centerRef = useRef(defaultCenter);
  const zoomRef = useRef(12);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Color constants
  const PRIMARY_COLOR = '#e28743';
  const SECONDARY_COLOR = '#304a3f';

  // Driver status colors using primary and secondary
  const statusColors = {
    active: '#10B981',
    on_duty: PRIMARY_COLOR,
    on_trip: '#8B5CF6',
    available: SECONDARY_COLOR,
    on_break: '#F59E0B',
    off_duty: '#6B7280',
    inactive: '#9CA3AF'
  };

  // Map options
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: true,
    fullscreenControl: true,
    mapTypeId: mapTypeId,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), [mapTypeId]);

  // Create custom marker icon
  const createDriverIcon = (status, isSelected = false) => {
    const color = statusColors[status] || '#6B7280';
    const scale = isSelected ? 1.3 : 1;
    
    return {
      path: window.google?.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 0.9,
      strokeColor: '#FFFFFF',
      strokeWeight: isSelected ? 3 : 2,
      scale: isSelected ? 14 : 12
    };
  };

  // Fetch drivers data
  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await driversAPI.getAll();
      
      // Handle different response structures
      if (response && response.success) {
        setDrivers(response.data || []);
      } else if (Array.isArray(response)) {
        setDrivers(response);
      } else if (response && Array.isArray(response.data)) {
        setDrivers(response.data);
      } else {
        throw new Error('Invalid response format');
      }
      
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError('Failed to load driver locations');
    } finally {
      setLoading(false);
    }
  };

  // Map load callback
  const onLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Filter drivers with valid locations
  const driversWithLocations = useMemo(() => 
    drivers.filter(driver => {
      if (!driver.currentLocation?.coordinates) return false;
      
      // Handle both [lng, lat] and {lat, lng} formats
      let lng, lat;
      if (Array.isArray(driver.currentLocation.coordinates)) {
        [lng, lat] = driver.currentLocation.coordinates;
      } else {
        lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
        lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
      }
      
      // Filter out coordinates that are 0,0 (default/null coordinates)
      return !(lng === 0 && lat === 0) && lng !== undefined && lat !== undefined;
    }), [drivers]);

  // Get default location drivers (without location data)
  const driversWithoutLocations = useMemo(() => 
    drivers.filter(driver => {
      if (!driver.currentLocation?.coordinates) return true;
      
      // Handle both [lng, lat] and {lat, lng} formats
      let lng, lat;
      if (Array.isArray(driver.currentLocation.coordinates)) {
        [lng, lat] = driver.currentLocation.coordinates;
      } else {
        lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
        lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
      }
      
      return (lng === 0 && lat === 0) || lng === undefined || lat === undefined;
    }), [drivers]);

  // Auto-zoom to show all drivers with locations
  const autoZoomToDrivers = useCallback(() => {
    if (!mapRef.current || driversWithLocations.length === 0) return;
    
    const bounds = new window.google.maps.LatLngBounds();
    
    driversWithLocations.forEach(driver => {
      // Handle both coordinate formats
      let lng, lat;
      if (Array.isArray(driver.currentLocation.coordinates)) {
        [lng, lat] = driver.currentLocation.coordinates;
      } else {
        lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
        lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
      }
      
      if (lng && lat) {
        bounds.extend({ lat, lng });
      }
    });
    
    // If we only have one driver, center and zoom on it
    if (driversWithLocations.length === 1) {
      const driver = driversWithLocations[0];
      let lng, lat;
      if (Array.isArray(driver.currentLocation.coordinates)) {
        [lng, lat] = driver.currentLocation.coordinates;
      } else {
        lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
        lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
      }
      
      if (lng && lat) {
        const position = { lat, lng };
        mapRef.current.setCenter(position);
        mapRef.current.setZoom(15);
        centerRef.current = position;
        zoomRef.current = 15;
      }
    } else {
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, right: 50, bottom: 50, left: 50 }
      });
    }
  }, [driversWithLocations]);

  // Auto-zoom on initial load
  useEffect(() => {
    if (!loading && driversWithLocations.length > 0) {
      setTimeout(autoZoomToDrivers, 500);
    }
  }, [loading, driversWithLocations, autoZoomToDrivers]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: PRIMARY_COLOR }}></div>
            <p className="text-gray-600">Loading driver locations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-red-500 mb-4">⚠️</div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchDrivers}
              className="px-4 py-2 text-white rounded-md"
              style={{ backgroundColor: PRIMARY_COLOR }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Live Fleet Overview</h2>
          <p className="text-gray-600 text-sm sm:text-base">Driver locations on map</p>
        </div>
        
        {/* Map Type Controls */}
        <div className="flex flex-wrap gap-2">
          <div className="flex space-x-1">
            <button 
              onClick={() => setMapTypeId('roadmap')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'roadmap' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'roadmap' ? PRIMARY_COLOR : undefined }}
            >
              Road
            </button>
            <button 
              onClick={() => setMapTypeId('satellite')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'satellite' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'satellite' ? PRIMARY_COLOR : undefined }}
            >
              Satellite
            </button>
            <button 
              onClick={() => setMapTypeId('hybrid')}
              className={`px-3 py-2 text-xs sm:text-sm rounded transition-colors ${
                mapTypeId === 'hybrid' 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{ backgroundColor: mapTypeId === 'hybrid' ? PRIMARY_COLOR : undefined }}
            >
              Hybrid
            </button>
          </div>
        </div>
      </div>

      {/* Stats - Mobile responsive grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100">
          <p className="text-xs sm:text-sm text-gray-600">Total Drivers</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{drivers.length}</p>
        </div>
        <div className="p-3 sm:p-4 rounded-lg border" style={{ 
          backgroundColor: `${PRIMARY_COLOR}10`,
          borderColor: `${PRIMARY_COLOR}20`
        }}>
          <p className="text-xs sm:text-sm text-gray-600">With Location</p>
          <p className="text-xl sm:text-2xl font-bold" style={{ color: PRIMARY_COLOR }}>{driversWithLocations.length}</p>
        </div>
        <div className="p-3 sm:p-4 rounded-lg border" style={{ 
          backgroundColor: `${SECONDARY_COLOR}10`,
          borderColor: `${SECONDARY_COLOR}20`
        }}>
          <p className="text-xs sm:text-sm text-gray-600">No Location</p>
          <p className="text-xl sm:text-2xl font-bold" style={{ color: SECONDARY_COLOR }}>{driversWithoutLocations.length}</p>
        </div>
      </div>

      {/* Google Map Container */}
      <div className="relative rounded-lg overflow-hidden border border-gray-200 h-[350px] sm:h-[400px] mb-4 sm:mb-6">
        <LoadScript
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
          loadingElement={<div className="h-full w-full bg-gray-100 animate-pulse" />}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={centerRef.current}
            zoom={zoomRef.current}
            options={mapOptions}
            onLoad={onLoad}
          >
            {/* Driver Markers with locations */}
            {driversWithLocations.map((driver) => {
              // Handle both coordinate formats
              let lng, lat;
              if (Array.isArray(driver.currentLocation.coordinates)) {
                [lng, lat] = driver.currentLocation.coordinates;
              } else {
                lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
                lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
              }
              
              if (!lng || !lat) return null;
              
              const position = { lat, lng };
              const driverName = driver.name || driver.user?.profile?.name || driver.user?.name || 'Driver';
              const isSelected = selectedDriver?._id === driver._id;

              return (
                <React.Fragment key={`driver-${driver._id || driver.id}`}>
                  <Marker
                    position={position}
                    icon={createDriverIcon(driver.status, isSelected)}
                    onClick={() => setSelectedDriver(driver)}
                  />
                  
                  {/* InfoWindow for selected driver */}
                  {isSelected && (
                    <InfoWindow
                      position={position}
                      onCloseClick={() => setSelectedDriver(null)}
                    >
                      <div className="p-2 min-w-[180px] sm:min-w-[200px]">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-sm text-gray-900">{driverName}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            driver.status === 'on_duty' 
                              ? `text-white` 
                              : 'bg-gray-100 text-gray-800'
                          }`} style={{ 
                            backgroundColor: driver.status === 'on_duty' ? PRIMARY_COLOR : undefined 
                          }}>
                            {driver.status?.toUpperCase()}
                          </span>
                        </div>
                        
                        {driver.currentLocation.address && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-600">Location:</p>
                            <p className="text-sm font-medium">📍 {driver.currentLocation.address}</p>
                          </div>
                        )}
                        
                        {driver.licenseNumber && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-600">License:</p>
                            <p className="text-sm font-medium">🚗 {driver.licenseNumber}</p>
                          </div>
                        )}
                        
                        <div className="mt-3 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            Last updated: {new Date(driver.currentLocation.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </React.Fragment>
              );
            })}
          </GoogleMap>
        </LoadScript>
      </div>

      {/* Driver List */}
      <div className="mt-4 sm:mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Driver Locations</h3>
        
        {/* Drivers with locations */}
        {driversWithLocations.length > 0 && (
          <div className="mb-4 sm:mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">📍 Drivers with Location Data</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {driversWithLocations.map((driver) => {
                // Handle both coordinate formats
                let lng, lat;
                if (Array.isArray(driver.currentLocation.coordinates)) {
                  [lng, lat] = driver.currentLocation.coordinates;
                } else {
                  lng = driver.currentLocation.coordinates?.lng || driver.currentLocation.coordinates?.longitude;
                  lat = driver.currentLocation.coordinates?.lat || driver.currentLocation.coordinates?.latitude;
                }
                
                const driverName = driver.name || driver.user?.profile?.name || driver.user?.name || 'Driver';
                
                return (
                  <div 
                    key={`driver-loc-${driver._id || driver.id}`}
                    className="bg-gray-50 p-3 sm:p-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-200"
                    onClick={() => {
                      if (mapRef.current && lng && lat) {
                        const position = { lat, lng };
                        mapRef.current.setCenter(position);
                        mapRef.current.setZoom(15);
                        setSelectedDriver(driver);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: statusColors[driver.status] || '#6B7280' }}
                          />
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{driverName}</h4>
                        </div>
                        
                        <div className="space-y-1 text-xs sm:text-sm">
                          <div className="flex flex-wrap gap-1 sm:gap-2 text-gray-600">
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {driver.status}
                            </span>
                            {driver.licenseNumber && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{ 
                                backgroundColor: `${SECONDARY_COLOR}10`,
                                color: SECONDARY_COLOR
                              }}>
                                {driver.licenseNumber}
                              </span>
                            )}
                          </div>
                          
                          {driver.currentLocation.address && (
                            <p className="text-gray-700 flex items-start gap-1 mt-2">
                              <span className="text-gray-500 mt-0.5 flex-shrink-0">📍</span>
                              <span className="truncate">{driver.currentLocation.address}</span>
                            </p>
                          )}
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Updated: {new Date(driver.currentLocation.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      
                      <button 
                        className="text-xs sm:text-sm ml-2 flex-shrink-0"
                        style={{ color: PRIMARY_COLOR }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Drivers without locations */}
        {driversWithoutLocations.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">❌ Drivers Awaiting Location Update</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {driversWithoutLocations.map((driver) => {
                const driverName = driver.name || driver.user?.profile?.name || driver.user?.name || 'Driver';
                
                return (
                  <div 
                    key={`driver-no-loc-${driver._id || driver.id}`}
                    className="p-3 sm:p-4 rounded-lg border"
                    style={{ 
                      backgroundColor: `${SECONDARY_COLOR}05`,
                      borderColor: `${SECONDARY_COLOR}20`
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-gray-400 flex-shrink-0" />
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{driverName}</h4>
                        </div>
                        
                        <div className="space-y-1 text-xs sm:text-sm">
                          <div className="flex flex-wrap gap-1 sm:gap-2 text-gray-600">
                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                              {driver.status}
                            </span>
                            {driver.licenseNumber && (
                              <span className="text-xs px-2 py-0.5 rounded" style={{ 
                                backgroundColor: `${SECONDARY_COLOR}10`,
                                color: SECONDARY_COLOR
                              }}>
                                {driver.licenseNumber}
                              </span>
                            )}
                          </div>
                          
                          <p className="text-sm mt-2" style={{ color: SECONDARY_COLOR }}>
                            ⚠️ Location not available
                          </p>
                          
                          <p className="text-xs text-gray-500 mt-2">
                            Last check-in: {driver.lastCheckIn ? new Date(driver.lastCheckIn).toLocaleDateString() : 'Never'}
                          </p>
                        </div>
                      </div>
                      
                      <span 
                        className="text-xs px-2 py-1 rounded ml-2 flex-shrink-0"
                        style={{ 
                          backgroundColor: `${SECONDARY_COLOR}20`,
                          color: SECONDARY_COLOR
                        }}
                      >
                        No GPS
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No drivers */}
        {drivers.length === 0 && (
          <div className="text-center py-8 sm:py-12 rounded-lg" style={{ backgroundColor: `${SECONDARY_COLOR}05` }}>
            <div className="text-3xl sm:text-4xl mb-3">🚗</div>
            <p className="text-gray-600">No drivers found</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Map Legend</h4>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          {Object.entries(statusColors).map(([status, color]) => (
            <div key={status} className="flex items-center gap-2">
              <div 
                className="w-4 h-4 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs sm:text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveFleetOverview;